import { UpdateEventBodyDto } from '@shared/core/events'
import { formatInTimeZone } from 'date-fns-tz'
import { Sparkles } from 'lucide-react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { FormattedText, Typography } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import type { EventData } from 'src/modules/event/types/event.types'
import { EventAutoActivationHelper } from 'src/modules/event/utils/EventAutoActivationHelper'
import classes from './edit-event-modal.module.scss'

type AutoActivationCalloutProps = {
  event: EventData
  onTurnOffReminder: (
    payload: Pick<UpdateEventBodyDto, 'active' | 'revoke_auto_activation' | 'standing_consent_revoke_source'>,
  ) => void | Promise<void>
  isTurnOffLoading?: boolean
}

export function AutoActivationCallout({ event, onTurnOffReminder, isTurnOffLoading }: AutoActivationCalloutProps) {
  const { formatMessage } = useIntl()
  const { timezone } = useDisplayTimezone()

  const hasMaterial = EventAutoActivationHelper.hasAutoActivationMaterial(event)

  const handleTurnOff = useCallback(() => {
    void onTurnOffReminder({
      active: false,
      revoke_auto_activation: true,
      standing_consent_revoke_source: 'auto_activation_callout',
    })
  }, [onTurnOffReminder])

  if (!hasMaterial) {
    return null
  }

  const reason = EventAutoActivationHelper.formatReason(
    event.auto_activation_reason_code,
    event.auto_activation_reason_params,
    formatMessage,
  )
  const activatedAt = event.auto_activated_at
    ? formatInTimeZone(new Date(event.auto_activated_at), timezone, 'MMM d, yyyy h:mm a')
    : null

  return (
    <div className={classes.autoActivationCallout} data-testid="auto-activation-callout">
      <div className={classes.autoActivationCalloutHeader}>
        <div className={classes.autoActivationCalloutTitleGroup}>
          <Typography variant="span" className={classes.autoActivationCalloutTitle}>
            <FormattedText id="autoActivation.callout.title" />
          </Typography>
          <Sparkles size={14} aria-hidden className={classes.autoActivationCalloutSparkles} />
        </div>
        {activatedAt && (
          <Typography variant="span" className={classes.autoActivationCalloutDate}>
            {activatedAt}
          </Typography>
        )}
      </div>
      <Typography variant="span" className={classes.autoActivationCalloutReason}>
        {reason}
      </Typography>
      <div className={classes.autoActivationCalloutFooter}>
        <Typography variant="span" className={classes.autoActivationCalloutFooterHelper}>
          <FormattedText id="autoActivation.callout.helper" />
        </Typography>
        {EventAutoActivationHelper.canUndoAutoActivation(event) && (
          <button
            type="button"
            className={classes.autoActivationTurnOffLink}
            onClick={handleTurnOff}
            disabled={isTurnOffLoading}
            data-testid="auto-activation-turn-off"
          >
            <FormattedText id="autoActivation.callout.turnOff" />
          </button>
        )}
      </div>
    </div>
  )
}
